import type Category from 'types/category';
import {
  categoryAccountNumberMatchesQuery,
  compareCategoriesByAccountNumber,
  isNumericCategorySearchQuery,
  normalizeAccountNumberForSearch,
} from './categoryUtils';

export type CategoryModalSearchOptions = {
  /** When false, account numbers are excluded from search (company setting off). */
  includeAccountNumber?: boolean;
};

/** Levenshtein edit distance (insert/delete/substitute), case-sensitive; pass lowercased inputs for fuzzy text. */
export function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = new Uint16Array(n + 1);
  let cur = new Uint16Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    cur[0] = i;
    const ai = a.charCodeAt(i - 1);
    for (let j = 1; j <= n; j++) {
      const cost = ai === b.charCodeAt(j - 1) ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
    }
    const t = prev;
    prev = cur;
    cur = t;
  }
  return prev[n];
}

function similarityFromDistance(
  aLen: number,
  bLen: number,
  dist: number,
): number {
  const denom = Math.max(aLen, bLen, 1);
  return Math.max(0, 1 - dist / denom);
}

function parentCategory(
  cat: Category,
  categories: Record<string, Category>,
): Category | undefined {
  const p = cat.parent;
  if (p === null || p === undefined) return undefined;
  const id = typeof p === 'string' ? p : p.id;
  return categories[id];
}

/** Root → leaf names (includes every ancestor name, for search keywords). */
export function categoryAncestorNamesPath(
  cat: Category,
  categories: Record<string, Category>,
): string[] {
  const path: string[] = [];
  let cur: Category | undefined = cat;
  const guard = new Set<string>();
  while (cur?.parent) {
    const p = parentCategory(cur, categories);
    if (!p || guard.has(p.id)) break;
    guard.add(p.id);
    path.unshift(p.name);
    cur = p;
  }
  return path;
}

/**
 * 0 = no match; higher = better. Uses name + ancestor names + tokenized words.
 * Tuned for short category labels (chart of accounts).
 */
export function scoreCategoryModalSearch(
  queryRaw: string,
  cat: Category,
  categories: Record<string, Category>,
  options?: CategoryModalSearchOptions,
): number {
  const q = queryRaw.trim().toLowerCase();
  if (!q) return 1;

  const includeAccountNumber = options?.includeAccountNumber !== false;
  if (
    includeAccountNumber &&
    categoryAccountNumberMatchesQuery(queryRaw, cat)
  ) {
    return 1;
  }

  const name = cat.name.trim().toLowerCase();
  const accountNumber = includeAccountNumber
    ? normalizeAccountNumberForSearch(cat.accountNumber)
    : '';
  const ancestors = categoryAncestorNamesPath(cat, categories);
  const pathStr = [...ancestors, name, accountNumber]
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  const tokens = pathStr.split(/\s+/).filter((t) => t.length >= 2);

  let best = 0;

  if (accountNumber) {
    if (accountNumber.includes(q)) {
      best = Math.max(best, 0.93);
    }
    if (accountNumber.startsWith(q)) {
      best = Math.max(best, 0.96);
    }
    best = Math.max(
      best,
      similarityFromDistance(
        q.length,
        accountNumber.length,
        levenshtein(q, accountNumber),
      ) * 0.95,
    );
  }

  // Strong: contiguous substring on name or full path
  if (name.includes(q)) {
    const bonus = Math.min(0.12, (q.length / Math.max(name.length, 1)) * 0.12);
    best = Math.max(best, 0.88 + bonus);
  }
  if (pathStr.includes(q)) {
    best = Math.max(best, 0.82);
  }

  // Prefix on name (autocomplete feel)
  if (name.startsWith(q)) {
    best = Math.max(best, 0.9);
  }

  // Levenshtein: whole name and whole path (path weighted slightly down)
  best = Math.max(
    best,
    similarityFromDistance(q.length, name.length, levenshtein(q, name)),
  );
  if (pathStr.length > 0 && pathStr.length <= 160) {
    best = Math.max(
      best,
      similarityFromDistance(
        q.length,
        pathStr.length,
        levenshtein(q, pathStr),
      ) * 0.94,
    );
  }

  // Keywords: each path segment and each token (parent names + words in labels)
  for (const segment of [...ancestors, name]) {
    const s = segment.trim().toLowerCase();
    if (!s) continue;
    if (s.includes(q)) best = Math.max(best, 0.8);
    best = Math.max(
      best,
      similarityFromDistance(q.length, s.length, levenshtein(q, s)) * 0.93,
    );
  }

  for (const word of tokens) {
    if (word.includes(q)) best = Math.max(best, 0.78);
    best = Math.max(
      best,
      similarityFromDistance(q.length, word.length, levenshtein(q, word)) * 0.9,
    );
  }

  // Multi-word queries: boost if every significant token hits something in the path
  const qParts = q.split(/\s+/).filter((p) => p.length >= 2);
  if (qParts.length >= 2) {
    const allPartsMatch = qParts.every(
      (part) =>
        pathStr.includes(part) ||
        tokens.some(
          (t) =>
            t.includes(part) ||
            similarityFromDistance(
              part.length,
              t.length,
              levenshtein(part, t),
            ) >= 0.65,
        ),
    );
    if (allPartsMatch) {
      best = Math.max(best, 0.75);
    }
  }

  return Math.min(best, 1);
}

function scoreQueryAgainstNameAndAccountNumber(
  queryRaw: string,
  nameRaw: string,
  accountNumberRaw: string | number | null | undefined,
  includeAccountNumber: boolean,
): number {
  const q = queryRaw.trim().toLowerCase();
  if (!q) return 0;

  const name = nameRaw.trim().toLowerCase();
  const accountNumber = includeAccountNumber
    ? normalizeAccountNumberForSearch(accountNumberRaw)
    : '';

  if (includeAccountNumber && accountNumber) {
    const probe = { accountNumber: accountNumberRaw } as Category;
    if (categoryAccountNumberMatchesQuery(queryRaw, probe)) {
      return 1;
    }
  }

  let best = 0;

  if (accountNumber) {
    if (accountNumber.includes(q)) {
      best = Math.max(best, 0.93);
    }
    if (accountNumber.startsWith(q)) {
      best = Math.max(best, 0.96);
    }
    best = Math.max(
      best,
      similarityFromDistance(
        q.length,
        accountNumber.length,
        levenshtein(q, accountNumber),
      ) * 0.95,
    );
  }

  if (name.includes(q)) {
    const bonus = Math.min(0.12, (q.length / Math.max(name.length, 1)) * 0.12);
    best = Math.max(best, 0.88 + bonus);
  }
  if (name.startsWith(q)) {
    best = Math.max(best, 0.9);
  }
  best = Math.max(
    best,
    similarityFromDistance(q.length, name.length, levenshtein(q, name)),
  );

  return Math.min(best, 1);
}

/** Match score using only the category's own name and account number. */
export function scoreCategoryDirectSearch(
  queryRaw: string,
  cat: Category,
  options?: CategoryModalSearchOptions,
): number {
  const includeAccountNumber = options?.includeAccountNumber !== false;
  return scoreQueryAgainstNameAndAccountNumber(
    queryRaw,
    cat.name,
    cat.accountNumber,
    includeAccountNumber,
  );
}

/** Match score using only the immediate parent's name and account number. */
export function scoreCategoryParentSearch(
  queryRaw: string,
  cat: Category,
  categories: Record<string, Category>,
  options?: CategoryModalSearchOptions,
): number {
  const parent = parentCategory(cat, categories);
  if (!parent) return 0;
  const includeAccountNumber = options?.includeAccountNumber !== false;
  return scoreQueryAgainstNameAndAccountNumber(
    queryRaw,
    parent.name,
    parent.accountNumber,
    includeAccountNumber,
  );
}

/**
 * Flat category picker sort (CategorySearchInput): empty query uses account
 * number order when any numbered accounts exist, else name; active query ranks
 * by direct name/number match before parent name/number, then full path score.
 */
export function sortCategoriesForFlatPicker(
  categories: Category[],
  queryRaw: string,
  categoriesMap: Record<string, Category>,
  options?: CategoryModalSearchOptions,
): Category[] {
  const query = queryRaw.trim();
  const sorted = [...categories];

  if (!query) {
    const includeAccountNumber = options?.includeAccountNumber !== false;
    const anyAccountNumbers =
      includeAccountNumber &&
      sorted.some((category) => (category.accountNumber?.trim() ?? '') !== '');

    return sorted.sort((a, b) => {
      if (anyAccountNumbers) {
        const byAccount = compareCategoriesByAccountNumber(a, b);
        if (byAccount !== 0) return byAccount;
      }
      return a.name.localeCompare(b.name);
    });
  }

  const searchOptions = options;
  return sorted.sort((a, b) => {
    const directA = scoreCategoryDirectSearch(query, a, searchOptions);
    const directB = scoreCategoryDirectSearch(query, b, searchOptions);
    if (directB !== directA) return directB - directA;

    const parentA = scoreCategoryParentSearch(
      query,
      a,
      categoriesMap,
      searchOptions,
    );
    const parentB = scoreCategoryParentSearch(
      query,
      b,
      categoriesMap,
      searchOptions,
    );
    if (parentB !== parentA) return parentB - parentA;

    const fullA = scoreCategoryModalSearch(
      query,
      a,
      categoriesMap,
      searchOptions,
    );
    const fullB = scoreCategoryModalSearch(
      query,
      b,
      categoriesMap,
      searchOptions,
    );
    if (fullB !== fullA) return fullB - fullA;

    return a.name.localeCompare(b.name);
  });
}

/** Minimum score to show a row; stricter on very short queries to limit noise */
export function minScoreForCategorySearchQuery(
  normalizedQuery: string,
): number {
  const trimmed = normalizedQuery.trim();
  const len = trimmed.length;
  if (len <= 0) return 0;
  if (isNumericCategorySearchQuery(trimmed)) {
    return 0.35;
  }
  if (len <= 2) return 0.799;
  if (len === 3) return 0.42;
  return 0.38;
}

/** For 1–2 chars: require substring in name or path (score alone is noisy). */
export function passesShortQueryHardGate(
  queryRaw: string,
  cat: Category,
  categories: Record<string, Category>,
  score: number,
  options?: CategoryModalSearchOptions,
): boolean {
  const q = queryRaw.trim().toLowerCase();
  if (q.length > 2) return true;
  if (q.length === 0) return true;
  const includeAccountNumber = options?.includeAccountNumber !== false;
  if (
    includeAccountNumber &&
    categoryAccountNumberMatchesQuery(queryRaw, cat)
  ) {
    return true;
  }
  const name = cat.name.trim().toLowerCase();
  const pathStr = [...categoryAncestorNamesPath(cat, categories), name]
    .join(' ')
    .toLowerCase();
  return name.includes(q) || pathStr.includes(q) || score >= 0.92;
}

export type CategoryModalListItem =
  { type: 'root'; root: Category } | { type: 'category'; category: Category };

/**
 * When searching: (1) sort categories within each root block by score desc;
 * (2) sort root blocks by preferred root order when provided, otherwise by the
 * strongest visible match inside that section (best section first).
 */
export function reorderCategoryModalItemsForSearch<
  T extends CategoryModalListItem,
>(items: T[], scores: Map<string, number>, preferredRootOrder?: string[]): T[] {
  type Segment = {
    root: Extract<T, { type: 'root' }>;
    cats: Extract<T, { type: 'category' }>[];
  };

  const segments: Segment[] = [];
  let i = 0;
  while (i < items.length) {
    const it = items[i];
    if (it.type !== 'root') {
      i++;
      continue;
    }
    const root = it as Segment['root'];
    i++;
    const cats: Segment['cats'] = [];
    while (i < items.length) {
      const next = items[i];
      if (next.type !== 'category') break;
      cats.push(next as Segment['cats'][number]);
      i++;
    }
    segments.push({ root, cats });
  }

  for (const seg of segments) {
    seg.cats.sort((a, b) => {
      const ca = (a as { type: 'category'; category: Category }).category;
      const cb = (b as { type: 'category'; category: Category }).category;
      const sa = scores.get(ca.id) ?? 0;
      const sb = scores.get(cb.id) ?? 0;
      if (sb !== sa) return sb - sa;
      return ca.name.localeCompare(cb.name);
    });
  }

  const preferredRootIndex = (rootName: string): number => {
    if (!preferredRootOrder?.length) {
      return Number.POSITIVE_INFINITY;
    }
    const key = rootName.trim().toLowerCase();
    const idx = preferredRootOrder.findIndex(
      (name) => name.trim().toLowerCase() === key,
    );
    return idx === -1 ? preferredRootOrder.length + 1 : idx;
  };

  segments.sort((a, b) => {
    const preferredA = preferredRootIndex(a.root.root.name);
    const preferredB = preferredRootIndex(b.root.root.name);
    if (preferredA !== preferredB) {
      return preferredA - preferredB;
    }
    const maxIn = (xs: Segment['cats']): number =>
      xs.length === 0
        ? 0
        : Math.max(
            ...xs.map(
              (x) =>
                scores.get(
                  (x as { type: 'category'; category: Category }).category.id,
                ) ?? 0,
            ),
          );
    const ma = maxIn(a.cats);
    const mb = maxIn(b.cats);
    if (mb !== ma) return mb - ma;
    return a.root.root.name.localeCompare(b.root.root.name);
  });

  const out: T[] = [];
  for (const seg of segments) {
    out.push(seg.root as T);
    out.push(...(seg.cats as T[]));
  }
  return out;
}
